import { ArrowLeft, Camera, ExternalLink, Loader2, Send } from "lucide-react";

import { Button } from "@/components/ui/button";

import { FileUploader } from "./FileUploader";

function PaymentLinkCard({ paymentLink }) {
  if (!paymentLink) {
    return (
      <div className="rounded-2xl bg-amber-50 border border-amber-300 p-5 text-sm text-amber-900" data-testid="no-payment-link-msg">
        Este especialista aún no tiene una liga de pago configurada. Comunícate con la clínica
        para completar tu pago; mientras tanto no podrás enviar la solicitud.
      </div>
    );
  }
  return (
    <div className="border rounded-2xl bg-white p-6 space-y-4" data-testid="payment-link-card">
      <h2 className="font-heading font-semibold">1. Realiza tu pago en Stripe</h2>
      <p className="text-sm text-muted-foreground">
        Paga de forma segura con la liga de pago de tu especialista. Se abrirá en una pestaña nueva.
      </p>
      <a href={paymentLink} target="_blank" rel="noreferrer" data-testid="stripe-payment-link">
        <Button className="rounded-full px-8">
          <ExternalLink className="h-4 w-4 mr-2" strokeWidth={1.5} /> Pagar en Stripe
        </Button>
      </a>
      <div className="rounded-xl bg-amber-50 border border-amber-300 p-4 flex gap-3" data-testid="screenshot-warning">
        <Camera className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
        <p className="text-sm text-amber-900">
          <span className="font-bold">Importante:</span> al completar tu pago,{" "}
          <span className="font-bold">toma una captura de pantalla</span> del comprobante en Stripe.
          La necesitarás para enviar tu solicitud.
        </p>
      </div>
    </div>
  );
}

function RequestSummary({ selectedPro, personal, edad, docsCount }) {
  return (
    <div className="border rounded-2xl bg-white p-6 space-y-3" data-testid="payment-summary">
      <h2 className="font-heading font-semibold text-lg">Resumen de tu solicitud</h2>
      <div className="text-sm space-y-1.5 text-muted-foreground">
        <p>
          <span className="font-semibold text-foreground">Médico:</span> {selectedPro?.name}
        </p>
        <p>
          <span className="font-semibold text-foreground">Paciente:</span> {personal.nombres} {personal.apellidos}
          {edad !== null && ` (${edad} años)`}
        </p>
        <p>
          <span className="font-semibold text-foreground">Documentos:</span> {docsCount} archivo(s)
        </p>
      </div>
    </div>
  );
}

export function StepPayment({ selectedPro, personal, edad, files, setFiles, requestId, busy, onBack, onSubmit }) {
  const paymentLink = selectedPro?.payment_link || selectedPro?.professional_profile?.payment_link || "";
  const proofs = files.filter((f) => f.kind === "payment_proof");
  const docs = files.filter((f) => f.kind !== "payment_proof");
  return (
    <div className="space-y-5">
      <RequestSummary selectedPro={selectedPro} personal={personal} edad={edad} docsCount={docs.length} />
      <PaymentLinkCard paymentLink={paymentLink} />
      <FileUploader
        requestId={requestId}
        kind="payment_proof"
        label="2. Sube tu comprobante de pago"
        hint="La captura de pantalla del pago realizado en Stripe (imagen o PDF)."
        files={files}
        onChange={setFiles}
      />
      <p className="text-xs text-muted-foreground">
        Al enviar, tu solicitud llegará a tu especialista, quien verificará tu comprobante de pago
        antes de revisar tus laboratorios y preparar tu receta.
      </p>
      <div className="flex justify-between pt-2">
        <Button variant="ghost" className="rounded-full" onClick={onBack} data-testid="wizard-step-5-back">
          <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} /> Atrás
        </Button>
        <Button
          className="rounded-full px-8"
          disabled={busy || proofs.length === 0}
          onClick={onSubmit}
          data-testid="wizard-submit-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" strokeWidth={1.5} /> Enviar solicitud</>}
        </Button>
      </div>
    </div>
  );
}
